import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import routes from 'src/Resources/routes.json';
import {
  TicketDetail as TicketDetailType,
  TicketStatus,
  TicketPriority,
  TicketCategory,
  AdminUser,
  subCategoryLabels,
  subCategoryKeys,
  subCategoriesByCategory,
  categoryLabels,
  categoryKeys,
  otherSubCategoryValues,
  getCategoryDisplayLabel,
  getSubCategoryDisplayLabel
} from 'src/interfaces/TicketTypes';
import Footer from 'src/components/Footer';
import StatusChip from './StatusChip';
import AiDiagnosisPanel from './AiDiagnosisPanel';
import CommentThread from './CommentThread';
import TimelinePanel from './TimelinePanel';
import FileUploadButton from 'src/components/FileUploadButton';
import DocumentList from 'src/components/DocumentList';

const priorityKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TicketPriority.Critical]: { id: 'priorityCritical', defaultMessage: 'Critical' },
  [TicketPriority.High]:     { id: 'priorityHigh',     defaultMessage: 'High' },
  [TicketPriority.Medium]:   { id: 'priorityMedium',   defaultMessage: 'Medium' },
  [TicketPriority.Low]:      { id: 'priorityLow',      defaultMessage: 'Low' }
};

const statusKeys: { value: number; id: string; defaultMessage: string }[] = [
  { value: TicketStatus.Open,       id: 'statusOpen',       defaultMessage: 'Open' },
  { value: TicketStatus.InProgress, id: 'statusInProgress', defaultMessage: 'In Progress' },
  { value: TicketStatus.Resolved,   id: 'statusResolved',   defaultMessage: 'Solved' }
];

function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const intl = useIntl();
  const [detail, setDetail] = useState<TicketDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [updatingAssignee, setUpdatingAssignee] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rootCause, setRootCause] = useState('');
  const [solution, setSolution] = useState('');
  const [savingResolution, setSavingResolution] = useState(false);
  const [resolutionError, setResolutionError] = useState('');
  const [resolutionSaved, setResolutionSaved] = useState(false);
  const [description, setDescription] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);
  const [descriptionSaved, setDescriptionSaved] = useState(false);
  const [docRefreshKey, setDocRefreshKey] = useState(0);
  const [solveGateOpen, setSolveGateOpen] = useState(false);
  const rootCauseRef = useRef<HTMLInputElement | null>(null);
  const solutionRef  = useRef<HTMLInputElement | null>(null);

  // Custom "Other" editing state
  const [editCustomSub, setEditCustomSub] = useState('');
  const [editCustomCat, setEditCustomCat] = useState('');
  const [customSubSuggestions, setCustomSubSuggestions] = useState<string[]>([]);
  const [customCatSuggestions, setCustomCatSuggestions] = useState<string[]>([]);

  const fetchDetail = useCallback(() => {
    if (!id) return;
    axiosConfig
      .get('/GetTicketDetail', { params: { id: Number(id) } })
      .then((res) => {
        setDetail(res.data);
        setRootCause(res.data.ticket.rootCause ?? '');
        setSolution(res.data.ticket.solution ?? '');
        setDescription(res.data.ticket.description ?? '');
        setEditCustomSub(res.data.ticket.customSubCategory ?? '');
        setEditCustomCat(res.data.ticket.customCategory ?? '');
        setError('');
      })
      .catch(() => setError('Failed to load ticket details.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchDetail();
    axiosConfig
      .get('/GetAdminUsers')
      .then((res) => setAdminUsers(res.data))
      .catch(() => {});
  }, [fetchDetail]);

  // Fetch custom subcategory suggestions when ticket's category changes
  useEffect(() => {
    if (!detail) return;
    axiosConfig
      .get('/GetCustomSubCategories', { params: { category: detail.ticket.category } })
      .then((res) => {
        if (Array.isArray(res.data)) setCustomSubSuggestions(res.data);
      })
      .catch(() => setCustomSubSuggestions([]));
  }, [detail?.ticket.category]);

  // Fetch custom category suggestions when "Other" category
  useEffect(() => {
    if (!detail || detail.ticket.category !== TicketCategory.Other) {
      setCustomCatSuggestions([]);
      return;
    }
    axiosConfig
      .get('/GetCustomCategories')
      .then((res) => {
        if (Array.isArray(res.data)) setCustomCatSuggestions(res.data);
      })
      .catch(() => setCustomCatSuggestions([]));
  }, [detail?.ticket.category]);

  const handleStatusChange = (newStatus: number) => {
    if (!detail) return;
    if (
      newStatus === TicketStatus.Resolved &&
      (!rootCause.trim() || !solution.trim())
    ) {
      setSolveGateOpen(true);
      return;
    }
    setResolutionError('');
    setUpdatingStatus(true);
    axiosConfig
      .put('/UpdateTicket', {
        ...detail.ticket,
        status: newStatus,
        rootCause,
        solution
      })
      .then(() => fetchDetail())
      .catch(() => setError('Failed to update status.'))
      .finally(() => setUpdatingStatus(false));
  };

  const handleAssigneeChange = (assigneeId: number | '') => {
    if (!detail) return;
    setUpdatingAssignee(true);
    axiosConfig
      .put('/UpdateTicket', {
        ...detail.ticket,
        assigneeId: assigneeId === '' ? null : assigneeId
      })
      .then(() => fetchDetail())
      .catch(() => setError('Failed to update assignee.'))
      .finally(() => setUpdatingAssignee(false));
  };

  const handleTicketFieldChange = (fields: Partial<typeof detail.ticket>) => {
    if (!detail) return;
    axiosConfig
      .put('/UpdateTicket', { ...detail.ticket, ...fields })
      .then(() => fetchDetail())
      .catch(() => setError('Failed to update ticket.'));
  };

  const handleDelete = () => {
    if (!id) return;
    setDeleting(true);
    axiosConfig
      .delete('/DeleteTicket', { params: { id: Number(id) } })
      .then(() => navigate('/tickets'))
      .catch(() => {
        setError('Failed to delete ticket.');
        setDeleting(false);
        setDeleteOpen(false);
      });
  };

  const handleSaveDescription = () => {
    if (!detail) return;
    setSavingDescription(true);
    setDescriptionSaved(false);
    axiosConfig
      .put('/UpdateTicket', { ...detail.ticket, description })
      .then(() => {
        fetchDetail();
        setDescriptionSaved(true);
        setEditingDescription(false);
      })
      .catch(() => setError('Failed to save description.'))
      .finally(() => setSavingDescription(false));
  };

  const handleSaveResolution = () => {
    if (!detail) return;
    setSavingResolution(true);
    setResolutionError('');
    setResolutionSaved(false);
    axiosConfig
      .put('/UpdateTicket', { ...detail.ticket, rootCause, solution })
      .then(() => {
        fetchDetail();
        setResolutionSaved(true);
      })
      .catch(() => setResolutionError('Failed to save resolution.'))
      .finally(() => setSavingResolution(false));
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !detail) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Ticket not found.'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tickets')}
          sx={{ mt: 2 }}
        >
          <FormattedMessage id="backToTickets" defaultMessage="Back to Tickets" />
        </Button>
      </Container>
    );
  }

  const { ticket, comments, diagnosis, timeline } = detail;

  const isOtherCategory = ticket.category === TicketCategory.Other;
  const isOtherSubCategory = otherSubCategoryValues.has(ticket.subCategory);
  const catDisplay = getCategoryDisplayLabel(ticket.category, ticket.customCategory);
  const subDisplay = getSubCategoryDisplayLabel(ticket.subCategory, ticket.customSubCategory);

  return (
    <div style={{ userSelect: 'none' }}>
      <Container maxWidth="xl" sx={{ mt: '20px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/tickets')}
          >
            <FormattedMessage id="backToTickets" defaultMessage="Back to Tickets" />
          </Button>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteOpen(true)}
          >
            <FormattedMessage id="deleteTicket" defaultMessage="Delete" />
          </Button>
        </Box>

        <Box mb={3}>
          <Typography variant="h3" gutterBottom>
            #{ticket.id} — {ticket.subject}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <StatusChip status={ticket.status} size="medium" />
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage(priorityKeys[ticket.priority] ?? { id: 'unknown', defaultMessage: '-' })} ·{' '}
              {catDisplay}
              {subDisplay !== 'Other' && subDisplay !== 'Unknown' && subDisplay !== 'General'
                ? ` · ${subDisplay}`
                : ticket.customSubCategory
                  ? ` · ${ticket.customSubCategory}`
                  : ''}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Left column: Description, AI Diagnosis, Comments */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title={
                  <FormattedMessage
                    id="description"
                    defaultMessage="Description"
                  />
                }
                action={
                  !editingDescription && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setEditingDescription(true)}
                    >
                      <FormattedMessage id="edit" defaultMessage="Edit" />
                    </Button>
                  )
                }
              />
              <Divider />
              <CardContent>
                {editingDescription ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      multiline
                      minRows={3}
                      fullWidth
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        setDescriptionSaved(false);
                      }}
                    />
                    <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                      {descriptionSaved && (
                        <Typography variant="body2" color="success.main">
                          <FormattedMessage id="descriptionSaved" defaultMessage="Description saved." />
                        </Typography>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingDescription(false);
                          setDescription(ticket.description ?? '');
                        }}
                      >
                        <FormattedMessage id="cancel" defaultMessage="Cancel" />
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveDescription}
                        disabled={savingDescription}
                      >
                        <FormattedMessage id="save" defaultMessage="Save" />
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {ticket.description || (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        <FormattedMessage
                          id="noDescription"
                          defaultMessage="No description provided."
                        />
                      </Typography>
                    )}
                  </Typography>
                )}

                <Box sx={{ mt: 2 }}>
                  <DocumentList
                    ticketId={ticket.id}
                    refreshKey={docRefreshKey}
                    canDelete={true}
                  />
                  <Box sx={{ mt: 1 }}>
                    <FileUploadButton
                      scope={0}
                      ticketId={ticket.id}
                      onUploaded={() => setDocRefreshKey((k) => k + 1)}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ mb: 3 }}>
              <AiDiagnosisPanel
                diagnosis={diagnosis}
                onRefresh={fetchDetail}
              />
            </Box>

            <Card sx={{ mb: 3 }}>
              <CardHeader
                title={
                  <FormattedMessage
                    id="resolution"
                    defaultMessage="Resolution"
                  />
                }
                subheader={
                  detail.ticket.preFilledFromAi ? (
                    <Typography variant="caption" color="info.main">
                      <FormattedMessage
                        id="preFilledFromAi"
                        defaultMessage="Pre-filled from AI diagnosis"
                      />
                    </Typography>
                  ) : undefined
                }
              />
              <Divider />
              <CardContent
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {ticket.status === TicketStatus.Resolved ||
                ticket.status === TicketStatus.Closed ? (
                  <>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        <FormattedMessage
                          id="rootCauseLabel"
                          defaultMessage="Root Cause"
                        />
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {ticket.rootCause || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        <FormattedMessage
                          id="solutionLabel"
                          defaultMessage="Solution"
                        />
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {ticket.solution || '-'}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <TextField
                      label={
                        <FormattedMessage
                          id="rootCauseLabel"
                          defaultMessage="Root Cause"
                        />
                      }
                      multiline
                      minRows={2}
                      fullWidth
                      value={rootCause}
                      onChange={(e) => setRootCause(e.target.value)}
                      error={
                        !!resolutionError && !rootCause.trim()
                      }
                      inputRef={rootCauseRef}
                    />
                    <TextField
                      label={
                        <FormattedMessage
                          id="solutionLabel"
                          defaultMessage="Solution"
                        />
                      }
                      multiline
                      minRows={2}
                      fullWidth
                      value={solution}
                      onChange={(e) => setSolution(e.target.value)}
                      error={
                        !!resolutionError && !solution.trim()
                      }
                      inputRef={solutionRef}
                    />
                    <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                      {resolutionSaved && (
                        <Typography variant="body2" color="success.main">
                          <FormattedMessage
                            id="resolutionSaved"
                            defaultMessage="Resolution saved successfully."
                          />
                        </Typography>
                      )}
                      {resolutionError && (
                        <Typography variant="body2" color="error.main">
                          {resolutionError}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveResolution}
                        disabled={
                          savingResolution ||
                          (!rootCause.trim() && !solution.trim())
                        }
                      >
                        <FormattedMessage
                          id="saveResolution"
                          defaultMessage="Save Resolution"
                        />
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            <CommentThread
              ticketId={ticket.id}
              comments={comments}
              onCommentAdded={fetchDetail}
              adminUsers={adminUsers}
            />
          </Grid>

          {/* Right column: Status, Assignee, Details, Timeline */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title={
                  <FormattedMessage
                    id="updateStatus"
                    defaultMessage="Update Status"
                  />
                }
              />
              <Divider />
              <CardContent
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>
                    <FormattedMessage id="status" defaultMessage="Status" />
                  </InputLabel>
                  <Select
                    value={ticket.status}
                    label="Status"
                    onChange={(e) =>
                      handleStatusChange(Number(e.target.value))
                    }
                    disabled={updatingStatus}
                  >
                    {statusKeys.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {intl.formatMessage({ id: opt.id, defaultMessage: opt.defaultMessage })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>
                    <FormattedMessage id="assignee" defaultMessage="Assignee" />
                  </InputLabel>
                  <Select
                    value={ticket.assigneeId ?? ''}
                    label="Assignee"
                    onChange={(e) =>
                      handleAssigneeChange(
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    disabled={updatingAssignee}
                  >
                    <MenuItem value="">
                      <em>
                        <FormattedMessage
                          id="unassigned"
                          defaultMessage="Unassigned"
                        />
                      </em>
                    </MenuItem>
                    {adminUsers
                      .filter((u) => {
                        const name = (u.name ?? '').toLowerCase();
                        return (
                          !name.includes('inesco energy master admin') &&
                          !name.includes('paal myhre')
                        );
                      })
                      .map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>
                    <FormattedMessage id="priority" defaultMessage="Priority" />
                  </InputLabel>
                  <Select
                    value={ticket.priority}
                    label="Priority"
                    onChange={(e) =>
                      handleTicketFieldChange({ priority: Number(e.target.value) })
                    }
                  >
                    {Object.entries(priorityKeys).map(([value, msg]) => (
                      <MenuItem key={value} value={Number(value)}>
                        {intl.formatMessage(msg)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>
                    <FormattedMessage id="category" defaultMessage="Category" />
                  </InputLabel>
                  <Select
                    value={ticket.category}
                    label="Category"
                    onChange={(e) => {
                      const newCat = Number(e.target.value);
                      const isNewOther = newCat === TicketCategory.Other;
                      const firstSub = subCategoriesByCategory[newCat]?.[0] ?? 0;
                      handleTicketFieldChange({
                        category: newCat,
                        subCategory: isNewOther ? 0 : firstSub,
                        customSubCategory: null,
                        customCategory: null
                      });
                    }}
                  >
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <MenuItem key={value} value={Number(value)}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Custom category label when "Other" category */}
                {isOtherCategory && (
                  <Autocomplete<string, false, false, true>
                    freeSolo
                    options={customCatSuggestions}
                    value={editCustomCat}
                    onInputChange={(_e, val) => setEditCustomCat(val)}
                    onBlur={() => {
                      if (editCustomCat !== (ticket.customCategory ?? '')) {
                        handleTicketFieldChange({ customCategory: editCustomCat || null });
                      }
                    }}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <FormattedMessage
                            id="customCategoryLabel"
                            defaultMessage="Category Label"
                          />
                        }
                        placeholder="Type category name..."
                      />
                    )}
                  />
                )}

                {/* Subcategory dropdown — hidden when category is "Other" */}
                {!isOtherCategory && (
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      <FormattedMessage id="subCategory" defaultMessage="Sub-Category" />
                    </InputLabel>
                    <Select
                      value={ticket.subCategory}
                      label="Sub-Category"
                      onChange={(e) => {
                        const newSub = Number(e.target.value);
                        handleTicketFieldChange({
                          subCategory: newSub,
                          customSubCategory: otherSubCategoryValues.has(newSub) ? ticket.customSubCategory : null
                        });
                      }}
                    >
                      {(subCategoriesByCategory[ticket.category] ?? []).map((sc) => (
                        <MenuItem key={sc} value={sc}>
                          {subCategoryKeys[sc] ? intl.formatMessage(subCategoryKeys[sc]) : subCategoryLabels[sc] ?? 'Unknown'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Custom subcategory when "Other" sub is selected */}
                {(isOtherSubCategory || isOtherCategory) && (
                  <Autocomplete<string, false, false, true>
                    freeSolo
                    options={customSubSuggestions}
                    value={editCustomSub}
                    onInputChange={(_e, val) => setEditCustomSub(val)}
                    onBlur={() => {
                      if (editCustomSub !== (ticket.customSubCategory ?? '')) {
                        handleTicketFieldChange({ customSubCategory: editCustomSub || null });
                      }
                    }}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <FormattedMessage
                            id="customSubCategoryLabel"
                            defaultMessage="Custom Sub-Category"
                          />
                        }
                        placeholder="Type or select from existing..."
                      />
                    )}
                  />
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardHeader
                title={
                  <FormattedMessage id="details" defaultMessage="Details" />
                }
              />
              <Divider />
              <CardContent
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage
                      id="installation"
                      defaultMessage="Installation"
                    />
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={detail.installationProduct != null && detail.ticket.installationId != null ? {
                      color: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    } : {}}
                    onClick={() => {
                      if (detail.installationProduct == null || detail.ticket.installationId == null) return;
                      const productRoutes: Record<number, string> = {
                        0: routes.installations,
                        1: routes.salidomo_installations,
                        2: routes.sodiohome_installations,
                        3: routes.sodistore_installations,
                        4: routes.sodistoregrid_installations,
                        5: routes.sodistorepro_installations
                      };
                      const prefix = productRoutes[detail.installationProduct] ?? routes.installations;
                      navigate(
                        prefix + routes.list + routes.installation + detail.ticket.installationId + '/' + routes.live
                      );
                    }}
                  >
                    {detail.installationName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage
                      id="createdBy"
                      defaultMessage="Created By"
                    />
                  </Typography>
                  <Typography variant="body2">
                    {detail.creatorName}
                  </Typography>
                </Box>
                {detail.assigneeName && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      <FormattedMessage
                        id="assignee"
                        defaultMessage="Assignee"
                      />
                    </Typography>
                    <Typography variant="body2">
                      {detail.assigneeName}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage
                      id="createdAt"
                      defaultMessage="Created"
                    />
                  </Typography>
                  <Typography variant="body2">
                    {new Date(ticket.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage
                      id="updatedAt"
                      defaultMessage="Updated"
                    />
                  </Typography>
                  <Typography variant="body2">
                    {new Date(ticket.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
                {ticket.resolvedAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      <FormattedMessage
                        id="resolvedAt"
                        defaultMessage="Resolved"
                      />
                    </Typography>
                    <Typography variant="body2">
                      {new Date(ticket.resolvedAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <TimelinePanel events={timeline} />
          </Grid>
        </Grid>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>
            <FormattedMessage
              id="confirmDeleteTicket"
              defaultMessage="Delete Ticket?"
            />
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <FormattedMessage
                id="confirmDeleteTicketMessage"
                defaultMessage="This will permanently delete this ticket, its comments, AI diagnosis, and timeline. This action cannot be undone."
              />
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={deleting}
            >
              <FormattedMessage id="deleteTicket" defaultMessage="Delete" />
            </Button>
          </DialogActions>
        </Dialog>

        {/* Solve-gate reminder dialog */}
        <Dialog open={solveGateOpen} onClose={() => setSolveGateOpen(false)}>
          <DialogTitle>
            <FormattedMessage
              id="solveGateTitle"
              defaultMessage="Root Cause and Solution required"
            />
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <FormattedMessage
                id="solveGateBody"
                defaultMessage="To mark this ticket as Solved, please fill in both Root Cause and Solution before saving."
              />
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                setSolveGateOpen(false);
                if (!rootCause.trim()) rootCauseRef.current?.focus();
                else solutionRef.current?.focus();
              }}
            >
              <FormattedMessage id="solveGateOk" defaultMessage="OK" />
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
      <Footer />
    </div>
  );
}

export default TicketDetailPage;
